/**
 * Tiny 8-bit sound effects for the title screen, synthesised with WebAudio.
 * The context is only created from a user gesture (browsers block autoplay),
 * and the on/off choice is remembered in this browser.
 */
const KEY = "clerk.sfx.v1";
const VOLUME = 0.045;

let ctx: AudioContext | null = null;
let enabled = readPref();

function readPref(): boolean {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

export const sfxEnabled = (): boolean => enabled;

export function setSfx(on: boolean): void {
  enabled = on;
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    /* not persisted; fine */
  }
}

function audio(): AudioContext | null {
  if (!enabled) return null;
  ctx ??= new AudioContext();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

type Wave = OscillatorType;

/** Schedule one note `at` seconds from now. */
function note(freq: number, at: number, dur: number, wave: Wave = "square", vol = VOLUME): void {
  const a = audio();
  if (!a) return;
  const t0 = a.currentTime + at;
  const osc = a.createOscillator();
  const gain = a.createGain();
  osc.type = wave;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(vol, t0);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(a.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

const seq = (notes: [freq: number, dur: number][], wave: Wave = "square"): void => {
  let t = 0;
  for (const [f, d] of notes) {
    if (f > 0) note(f, t, d * 0.95, wave);
    t += d;
  }
};

export const sfx = {
  /** Cursor moved. */
  move: (): void => note(988, 0, 0.04),
  /** Confirm. */
  select: (): void =>
    seq([
      [784, 0.05],
      [1175, 0.09],
    ]),
  /** Back / cancel. */
  back: (): void =>
    seq([
      [587, 0.05],
      [392, 0.08],
    ]),
  /** "Press start" jingle: a rising arpeggio. */
  start: (): void =>
    seq([
      [523, 0.07],
      [659, 0.07],
      [784, 0.07],
      [1047, 0.07],
      [0, 0.04],
      [784, 0.07],
      [1047, 0.22],
    ]),
  /** Easter-egg fanfare. */
  fanfare: (): void =>
    seq([
      [784, 0.09],
      [784, 0.09],
      [784, 0.09],
      [988, 0.28],
      [880, 0.09],
      [988, 0.09],
      [1175, 0.4],
    ]),
};
